import React, { useContext, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ArrowRightStartOnRectangleIcon,
} from '@heroicons/react/24/outline';
import ConfirmModal from '../utils/ConfirmModal';
import { useAuth } from '../../contexts/AppContext';
import AppContext from '../../contexts/AppContext';

const PAGE_TITLES = {
  '/': { title: 'Dashboard', subtitle: 'Manage your digital compendium' },
  '/services/content': { title: 'Content', subtitle: '' },
  '/services/layout': { title: 'Services Layout', subtitle: 'Configure the service section presentation' },
  '/home/layout': { title: 'Home Layout', subtitle: 'Configure your home screen structure' },
  '/home/quick-links': { title: 'Quick Links', subtitle: 'Manage highlighted guest shortcuts' },
  '/settings/brand-style': { title: 'Brand Style', subtitle: 'Manage your digital compendium' },
  '/settings/display-collateral': { title: 'Display Collateral', subtitle: 'Manage your digital compendium' },
  '/settings/theme': { title: 'Venue Branding & Media Management', subtitle: 'Manage your digital compendium' },
  '/settings/contact': { title: 'Contact', subtitle: 'Manage your digital compendium' },
  '/ts/venue/branding': { title: 'Venue Branding & Media Management', subtitle: 'Manage your touchscreen' },
  '/ts/venue/content': { title: 'Venue Content Management', subtitle: 'Manage your touchscreen' },
  '/ts/venue/analytics': { title: 'Touchscreen Analytics', subtitle: 'Review kiosk traffic, engagement, and venue content performance' },
  '/ts/venue/imported-events': { title: 'Imported Events', subtitle: 'Manage your touchscreen' },
  '/analytics': { title: 'Analytics', subtitle: 'Review audience and content performance' },
  '/notifications': { title: 'Notifications', subtitle: 'Send short updates to guests who opted in' },
  '/map': { title: 'Map', subtitle: 'Manage map and location touchpoints' },
  '/account/add-user': { title: 'Add Users', subtitle: 'Manage your digital compendium' },
  '/user/add-user': { title: 'Add Users', subtitle: 'Manage your digital compendium' },
  '/billing': { title: 'Billing', subtitle: 'Manage your digital compendium' },
};

const getDisplayName = (userInfo) => {
  const candidates = [
    userInfo?.user?.alias,
    userInfo?.user?.accountName,
    userInfo?.user?.firstName,
    userInfo?.user?.name,
    userInfo?.user?.email,
  ];

  return candidates.find((value) => typeof value === 'string' && value.trim())?.trim() || 'User';
};

const getInitials = (displayName) => {
  return String(displayName)
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('') || 'U';
};

const TopNavbar = () => {
  const { selectedRole, userInfo } = useContext(AppContext);
  const { logout } = useAuth();
  const { pathname } = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const headerCopy = useMemo(() => {
    const baseCopy = PAGE_TITLES[pathname] || PAGE_TITLES['/'];

    if (pathname === '/user/add-user' || pathname === '/account/add-user') {
      return {
        ...baseCopy,
        subtitle: selectedRole?.type === 'tsVenue' ? 'Manage your touchscreen' : 'Manage your digital compendium',
      };
    }

    return baseCopy;
  }, [pathname, selectedRole?.type]);

  const userDisplayName = useMemo(() => getDisplayName(userInfo), [userInfo]);
  const userInitials = useMemo(() => getInitials(userDisplayName), [userDisplayName]);
  const handleConfirmLogout = () => {
    setIsModalOpen(false);
    logout();
  };

  return (
    <nav className="relative shrink-0 overflow-hidden bg-[linear-gradient(135deg,#101d6b_0%,#16257b_50%,#2138a1_100%)] text-white shadow-[0_12px_30px_rgba(16,29,107,0.18)]">
      <div className="absolute inset-0 opacity-20">
        <div className="absolute -left-12 top-0 h-28 w-28 rounded-full bg-white/18 blur-3xl" />
        <div className="absolute right-10 top-2 h-20 w-20 rounded-full bg-[#9fb7ff]/30 blur-2xl" />
      </div>

      <div className="relative mx-auto flex h-[84px] w-[92%] max-w-[1380px] items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="cms-page-title text-white">
            {headerCopy.title}
          </div>
          {headerCopy.subtitle ? (
            <div className="cms-page-subtitle mt-1.5 text-white/70">
              {headerCopy.subtitle}
            </div>
          ) : null}
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            title={userDisplayName}
            className="group inline-flex items-center gap-3 rounded-full border border-white/16 bg-white/8 px-2.5 py-2 text-left transition-all duration-200 hover:-translate-y-0.5 hover:bg-white/14 hover:shadow-[0_10px_22px_rgba(7,19,71,0.18)]"
            onClick={() => setIsModalOpen(true)}
          >
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#7f90ff] text-[12px] font-semibold tracking-[0.05em] transition-transform duration-200 group-hover:scale-105">
              {userInitials}
            </span>
            <span className="cms-button-text hidden pr-2 text-white/85 md:block">
              Logout
            </span>
            <ArrowRightStartOnRectangleIcon className="h-4 w-4 text-white/85 transition-transform duration-200 group-hover:translate-x-0.5" />
          </button>
        </div>
      </div>

      <ConfirmModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirmLogout}
        header="Confirm Logout"
        content="Are you sure you want to log out?"
      />
    </nav>
  );
};

export default TopNavbar;


